import React from "react"

import {MC} from "./MC.js"
import {MCHistory} from "./MCHistory.js"
import {Widget} from "./Widget.jsx"
import {Feedbacks} from "./Feedbacks.jsx"

// one observer for all embedded forms (embedded forms, embedded and modal dialogs) instead of window resize listener in each of them,
// it reports also width changes of their container without window resize (e.g. collapsed panel)
const embeddedResize = {
  observer: null,
  forms: new Map(),
  observe(element, form) {
    if (typeof ResizeObserver === "undefined" || !element) {
      return false
    }
    if (!this.observer) {
      this.observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
          this.forms.get(entry.target)?.runResize()
        }
      })
    }
    this.forms.set(element, form)
    this.observer.observe(element)
    return true
  },
  unobserve(element) {
    if (this.forms.delete(element)) {
      this.observer.unobserve(element)
    }
  }
}

class Form extends React.Component {

  state = {width: this.props.element.offsetWidth};
  serverEvents = {}
  worker = null
  formRef = React.createRef()
  ticking = false

  componentDidMount() {
    window.addEventListener("message", this.onMessage)
    if (this.props.embedded && embeddedResize.observe(this.props.element, this)) {
      this.observedElement = this.props.element
    } else {
      window.addEventListener("resize", this.runResize)
    }
    // pagehide (not beforeunload) - beforeunload is unreliable on mobile and breaks bfcache
    window.addEventListener("pagehide", this.onPageHide)
    window.addEventListener("pageshow", this.onPageShow)
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    if (MC.hasLogicForEvent(this.props.form, "scroll")) {
      window.addEventListener("scroll", this.runScroll, {passive: true})
    }
    this.runReadyConditional(true)
    if (this.props.element.offsetWidth != this.state.width && !this.props.stopRender) {
      this.setState({width: this.props.element.offsetWidth})
    }
    this.props.form.reactForm = this
  }

  shouldComponentUpdate(nextProps) {
    return nextProps.stopRender !== true
  }

  componentDidCatch(error) {
    this.setState(null) // to avoid react bundaries error
    if (this.props.form.flow) {
      this.props.form.flow.endOperationException('SYS_UnrecoverableRuntimeExc', error.message)
    } else {
      MC.error(error)
    }
  }

  handleSubmitDefault = (e) => {
    e.preventDefault()
  }

  runReadyConditional(mount = false) {
    if (MC.isModelerActive(this.props.form)) {
      return
    }
    if (this.props.form.flow.getRootFlow().runReady || mount) { // back to form by form action or remount after layout rebuild should reinit logic timers and server events
      this.props.form.flow.initLogicTimers()
      this.syncServerEvents()
    }
    if (this.props.form.flow.getRootFlow().runReady) {
      this.props.form.flow.getRootFlow().runReady = false
      if (MC.isFunction(this.props.form.flow.beforeReadyForm)) { // e.g. embedded form emits datachange before ready
        this.props.form.flow.beforeReadyForm(this.props.form)
      }
      if (MC.isFunction(this.props.form.flow.eventForm)) {
        this.props.form.flow.eventForm(null, 'ready')
      }
      if (this.hasEmbeddedDialog()) { // only embedded dialogs wait for this message, every form receives it
        var urlarr = window.location.href.split("/");
        window.postMessage({name: 'MNC-INTERNAL.READY-RUN', instanceId: this.props.form.flow.instanceId}, urlarr[0] + "//" + urlarr[2])
      }
      this.props.form.flow.reactFlow().writeReady()
    }
    if (MC.isFunction(this.props.form.flow.afterRenderForm)) { // this should be called after every rerender
      this.props.form.flow.afterRenderForm(this.props.form)
    }
  }

  componentWillUnmount() {
    this.props.form.flow.clearLogicTimers()
    if (this.observedElement) {
      embeddedResize.unobserve(this.observedElement)
    } else {
      window.removeEventListener("resize", this.runResize)
    }
    window.removeEventListener("message", this.onMessage)
    window.removeEventListener("pagehide", this.onPageHide)
    window.removeEventListener("pageshow", this.onPageShow)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    if (MC.hasLogicForEvent(this.props.form, "scroll")) {
      window.removeEventListener("scroll", this.runScroll, {passive: true})
    }
    this.unregisterServerEvents()
  }

  hasEmbeddedDialog() {
    const find = (field) => field.widget === 'embeddeddialog' || Array.isArray(field.fields) && field.fields.some(find)
    return find(this.props.form)
  }

  onMessage = (e) => {
    if (e.data && MC.isPlainObject(e.data)) {
      var name = e.data.name;
      if (!MC.isNull(name) && name != '' && MC.hasLogicForEvent(this.props.form, name)) { // every form on the page receives all messages
        if (MC.isFunction(this.props.form.flow.eventForm)) {
          this.props.form.flow.eventForm(null, name, null, e.data);
        }
      }
    }
  };

  // window resize for root form, resize observer for embedded form - only width matters, observer reports also the first
  // observation and height changes
  runResize = () => {
    if (this.props.element.offsetWidth == this.state.width) {
      return
    }
    this.setState({width: this.props.element.offsetWidth})
    this.props.form.flow.eventForm(null, 'resize')
  }

  runScroll = () => {
    let self = this
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        self.props.form.flow.eventForm(null, 'scroll')
        this.ticking = false
      })
      this.ticking = true
    }
  }

  componentDidUpdate() {
    this.props.form.reactForm = this
    this.runReadyConditional()
  }

  getResolution(field) {
    if (MC.isModelerReactAvailable(field)) {
      var modelerReact = MC.getModelerReact(field);
      return modelerReact.state.resolution;
    }
    return MC.getResolutionFromWidth(this.state.width, this.props.mconf);
  }

  buildSubFields(field) {
    let reslog = 'Form ' + this.props.form.id + ', width: ' + this.state.width
    let resolution = this.getResolution(field)
    reslog += ', resolution: ' + resolution
    resolution = MC.getAvailableResolution(resolution, field)
    reslog += ', used resolution: ' + (resolution ? resolution : 'autolayout')
    if (this.prevreslog !== reslog) {
      this.prevreslog = reslog
      MCHistory.log(MCHistory.T_INFO, reslog, field.flow.debug('DETAIL'))
    }
    return MC.renderRows(field.fields, resolution)
  }

  onPageHide = () => {
    this.unregisterServerEvents() // releases the connection in the shared worker, so it is not kept alive for a frozen or unloaded page
  }

  onPageShow = (e) => {
    if (e.persisted && !MC.isModelerActive(this.props.form)) { // restored from bfcache, react does not remount, so subscriptions dropped on pagehide have to be renewed explicitly
      this.syncServerEvents()
    }
  }

  onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && !MC.isModelerActive(this.props.form)) {
      this.reassertServerEvents() // while the tab was hidden its ping could have been throttled enough for the shared worker to prune the port as dead
    }
  }

  serverEventsBaseUrl() {
    return this.props.form.flow.baseUrl // the same base as the flow api and the shared worker script use, mconf.baseUrl can be overriden by the baseUrl prop
  }

  syncServerEvents() {
    const wanted = {}
    const serverEvents = MC.getFieldParamValue(this.props.form.param, 'serverEvents*')
    if (serverEvents) {
      for (let event of MC.asArray(serverEvents)) {
        wanted[event.type + '$' + (event.key ? event.key : '') + '$' + (event.itemId ? event.itemId : '')] = true
      }
    }
    for (let key of Object.keys(this.serverEvents)) { // subscriptions which are still wanted are kept alive, reconnecting them on every runReady churns the shared connection and leaves stale sinks on server
      if (!wanted[key]) {
        this.stopServerEvent(key)
      }
    }
    for (let key in wanted) {
      if (!this.serverEvents[key]) {
        this.startServerEvent(key)
      }
    }
    if (Object.keys(this.serverEvents).length === 0) {
      this.unregisterServerEvents() // nothing left to listen to, the worker port and its pinging are not needed
    }
  }

  reassertServerEvents() {
    if (!this.worker) {
      return
    }
    this.worker.port.postMessage("PING")
    for (let key of Object.keys(this.serverEvents)) {
      this.worker.port.postMessage("START:" + this.serverEvents[key] + '@' + key) // subscribing again is a no-op for a live subscription, it only renews the pruned ones
    }
  }

  startServerEvent(key) {
    let self = this
    const ssePath = this.serverEventsBaseUrl() + 'miniapp/sse/'
    if (typeof(SharedWorker) !== "undefined") {
      if (!this.worker) {
        this.worker = new SharedWorker(this.serverEventsBaseUrl() + 'miniapp/lib/sharedWorker.js')
        this.worker.port.onmessage = function (e) {
          self.onServerMessage(e)
        }
        this._pingIntervalId = setInterval(() => {
          this.worker.port.postMessage("PING")
        }, 15000)
      }
      this.worker.port.postMessage("START:" + ssePath + '@' + key)
      this.serverEvents[key] = ssePath // remembered, so that stop addresses the same path even when the base url has changed meanwhile
    } else {
      let source = new EventSource(ssePath + key)
      source.addEventListener(key, (e) => { self.onServerMessage(e) }, false)
      this.serverEvents[key] = source
    }
  }

  stopServerEvent(key) {
    const subscription = this.serverEvents[key]
    delete this.serverEvents[key]
    if (typeof(SharedWorker) !== "undefined") {
      this.worker?.port.postMessage("STOP:" + subscription + '@' + key)
    } else {
      subscription.close()
    }
  }

  onServerMessage(e) {
    if (e.data) {
      if (typeof e.data === 'string') {
        if (e.data.startsWith('RESUBSCRIBE:')) {
          this.reassertServerEvents()
        } else if (e.data.startsWith('{')) {
          const tokens = e.type.split('$')
          let data = {type: tokens[0], key: tokens[1], itemId: tokens[2]}
          data.data = JSON.parse(e.data).props
          if (MC.isFunction(this.props.form.flow.eventForm)) {
            this.props.form.flow.eventForm(null, tokens[0], null, data)
          }
        } if (e.data.startsWith('ERROR:') || e.data.startsWith('RECONNECT:')) {
          let urlarr = window.location.href.split("/")
          window.postMessage({name: e.data.startsWith('ERROR:') ? 'serverConnectionLostEvent' : 'serverConnectionBackEvent'}, urlarr[0] + "//" + urlarr[2]);
        }
      } else if (MC.isPlainObject(e.data)) {
        if (MC.isFunction(this.props.form.flow.eventForm)) {
          this.props.form.flow.eventForm(null, e.data.type, null, e.data)
        }
      }
    }
  }

  unregisterServerEvents() {
    for (let key of Object.keys(this.serverEvents)) {
      this.stopServerEvent(key)
    }
    if (this.worker) {
      this.worker.port.close()
      delete this.worker
      clearInterval(this._pingIntervalId)
      delete this._pingIntervalId
    }
    this.serverEvents = {}
  }

  render() {
    let cssclass = MC.classes('mnc form', this.props.form.cssclass, MC.getFieldParamValue(this.props.form.param, '@cssClass'))
    let invalidSummary
    if (this.props.form.invalidSummary && !MC.isModelerActive(this.props.form) && MC.getFieldParamBooleanValue(this.props.form.param, '@showInvalidSummary')) {
      let errs = []
      for (let path in this.props.form.invalidSummary) {
        errs.push(<div className="item" key={path}>{this.props.form.invalidSummary[path]}</div>)
      }
      if (errs.length > 0) {
        invalidSummary = <div className="mnc negative message"><div className="mnc list">{errs}</div></div>
      }
    }
    var interactive;
    if (MC.isModelerReactAvailable(this.props.form)) {
      var modelerReact = MC.getModelerReact(this.props.form)
      var interactiveField = modelerReact.state.interactiveField;
      if (interactiveField) {
        var resolution = this.getResolution(interactiveField);
        resolution = MC.getAvailableResolution(resolution, this.props.form);
        interactive = <div className="mnc row"><Widget widget={interactiveField} resolution={resolution}/></div>
      }
    }
    let RootElem = this.props.embedded ? 'div' : 'form'
    let gridStyle = MC.isModelerActive(this.props.form) ? {margin: 0} : {}
    return (
      <RootElem key={this.props.form.flow.formExecutionId} ref={this.formRef} className={cssclass} style={MC.styleObjectFromString(MC.getFieldParamValue(this.props.form.param, '@css'))} role="form" id={this.props.form.id} onSubmit={this.handleSubmitDefault} data-widget-id={this.props.form.id} tabIndex="-1">
        {Array.isArray(this.props.form.feedback) && this.props.form.feedback.length > 0 && <Feedbacks feedbacks={this.props.form.feedback} position="inline" />}
        {invalidSummary}
        <div className="mnc twelve column grid" style={gridStyle}>
          {this.buildSubFields(this.props.form)}
        </div>
        {interactive}
      </RootElem>)
  }

}

export {Form}